import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { createServiceNotes } from '@/modules/Service/services'
import { ServiceUpdate } from '@/modules/Service/types'
import { ModalProps } from '@/types/shared'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem } from 'vnetwork-ui'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface CreateServiceNoteProps extends ModalProps {
    serviceID: number
}
const CreateServiceNote: React.FC<CreateServiceNoteProps> = ({
    open,
    onClose,
    serviceID,
}) => {
    const { t } = useTranslation()
    const { control, handleSubmit } = useForm<Partial<ServiceUpdate>>({
        defaultValues: {
            service_update_description: '',
        },
    })
    const { refetch } = useQueryClientCallback({
        reactQueryKeys: [QUERY_KEYS.SERVICES],
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: createServiceNotes,
        },
        {
            successMessage: t('create-success'),
            reactQueryKeys: QUERY_KEYS.SERVICES,
            onSuccess: () => {
                onClose()
                refetch()
            },
        }
    )

    const onSubmitHandler = async (data: Partial<ServiceUpdate>) => {
        if (!data?.service_update_description?.trim()) return

        mutateAsync({
            serviceID,
            data,
        })
    }

    return (
        <Dialog open={open} onClose={onClose} title={t('create-service-note')}>
            <form
                onSubmit={handleSubmit(onSubmitHandler)}
                className="space-y-4 mt-2"
            >
                <FormItem
                    control={control}
                    name="service_update_description"
                    type="textarea"
                    label={t('content')}
                    placeholder={t('enter-content')}
                />

                <div className="flex items-center justify-end gap-2">
                    <Button variant="outlined" onClick={onClose} type="button">
                        {t('cancel')}
                    </Button>
                    <Button
                        loading={isPending}
                        disabled={isPending}
                        type="submit"
                    >
                        {t('save')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default CreateServiceNote
